import { CHANNEL_TYPES } from '@/vars/general'
import type { Gcb2ChatListType } from '@/api/messenger/types'

export type ChatChannelKey = 'all' | 'wa' | 'tg' | 'max' | 'maxb' | 'sms'

export interface ChatChannelConfig {
    key: ChatChannelKey
    name: string
    short: string
    /** Цвет фона иконки в фильтрах чата (как в макете) */
    color: string
    avatarType: string
}

export interface ConnectorAvatarLike {
    type: string
    color: string
    symbol: string
    image: string | null
    name: string
}

/** Цвета фильтров каналов — как в макете DH Единый чат */
export const CHAT_CHANNELS: Record<Exclude<ChatChannelKey, 'all'>, ChatChannelConfig> = {
    wa: {
        key: 'wa',
        name: 'WhatsApp',
        short: 'WA',
        color: '#25D366',
        avatarType: CHANNEL_TYPES.whatsappChannel,
    },
    tg: {
        key: 'tg',
        name: 'Telegram',
        short: 'TG',
        color: '#229ED9',
        avatarType: CHANNEL_TYPES.telegramChannel,
    },
    max: {
        key: 'max',
        name: 'MAX',
        short: 'MAX',
        color: '#7B57D4',
        avatarType: CHANNEL_TYPES.maxChannel,
    },
    maxb: {
        key: 'maxb',
        name: 'MAX Business',
        short: 'MAXB',
        color: '#6A3FC0',
        avatarType: CHANNEL_TYPES.maxBusiness,
    },
    sms: {
        key: 'sms',
        name: 'SMS',
        short: 'SMS',
        color: '#4F8EF7',
        avatarType: CHANNEL_TYPES.smsChannel,
    },
}

/** $dark-new — фон иконки «Все» в фильтре каналов */
export const CHAT_FILTER_ALL_COLOR = '#1A1A2E'

export const CHANNEL_TABS: Array<{ key: ChatChannelKey; label: string }> = [
    { key: 'all', label: 'Все' },
    { key: 'wa', label: 'WhatsApp' },
    { key: 'tg', label: 'Telegram' },
    { key: 'max', label: 'MAX' },
    { key: 'maxb', label: 'MAX Business' },
    { key: 'sms', label: 'SMS' },
]

export const AVATAR_COLORS = [
    '#E8743B',
    '#5C6BC0',
    '#2E9E6B',
    '#9C5BC0',
    '#0E9DB5',
    '#D2477E',
    '#3F7FE0',
    '#C77F2E',
    '#5BA86B',
    '#E0843B',
]

const PROVIDER_TO_CHANNEL: Record<string, Exclude<ChatChannelKey, 'all'>> = {
    // WhatsApp
    whatsapp: 'wa',
    'whatsapp-basis': 'wa',
    whatsappbasis: 'wa',
    'whatsapp-api': 'wa',
    whatsappapi: 'wa',
    'basis-api': 'wa',
    'wapi-whatsapp': 'wa',
    'dh-whatsapp': 'wa',
    // Telegram
    telegram: 'tg',
    'telegram-api': 'tg',
    telegramapi: 'tg',
    'telegram-dh': 'tg',
    telegramdh: 'tg',
    'wapi-telegram': 'tg',
    'dh-telegram': 'tg',
    // MAX
    max: 'max',
    'max-basis': 'max',
    maxbasis: 'max',
    'max-dh': 'max',
    maxdh: 'max',
    'green-api-max': 'max',
    'dh-max': 'max',
    // MAX Business
    'max-business': 'maxb',
    maxbusiness: 'maxb',
    // SMS
    sms: 'sms',
    'sms-gate': 'sms',
    smsgate: 'sms',
}

export function providerToChannelKey(provider?: string | null): Exclude<ChatChannelKey, 'all'> {
    if (!provider) return 'maxb'
    const normalized = provider.toLowerCase().replace(/_/g, '-')
    const mapped = PROVIDER_TO_CHANNEL[normalized]
    if (mapped) return mapped

    if (normalized.includes('max-business') || normalized.includes('maxbusiness')) return 'maxb'
    if (normalized.includes('telegram')) return 'tg'
    if (normalized.includes('whatsapp') || normalized.includes('basis-api')) return 'wa'
    if (normalized.includes('sms')) return 'sms'
    if (normalized.includes('max')) return 'max'

    return 'maxb'
}

/** provider из API (dh-telegram, max-business, …) или appId чата */
export function resolveChatProvider(
    provider?: string | null,
    appId?: string | null
): string | null {
    return provider || appId || null
}

export function getContactChannelKey(
    provider?: string | null,
    appId?: string | null
): Exclude<ChatChannelKey, 'all'> {
    return providerToChannelKey(resolveChatProvider(provider, appId))
}

export function channelKeyToApiType(key: ChatChannelKey): Gcb2ChatListType | undefined {
    switch (key) {
        case 'tg':
            return 'telegram'
        case 'maxb':
            return 'max-business'
        case 'wa':
            return 'whatsapp'
        case 'max':
            return 'max'
        case 'sms':
            return 'sms'
        default:
            return undefined
    }
}

export function apiTypeToChannelKey(type: string): ChatChannelKey {
    switch (type) {
        case 'telegram':
            return 'tg'
        case 'max-business':
            return 'maxb'
        case 'whatsapp':
            return 'wa'
        case 'max':
            return 'max'
        case 'sms':
            return 'sms'
        case 'all':
            return 'all'
        default:
            return 'all'
    }
}

export function matchesChannelFilter(
    channelKey: ChatChannelKey,
    provider?: string | null,
    appId?: string | null
): boolean {
    if (channelKey === 'all') return true
    const resolved = resolveChatProvider(provider, appId)
    if (!resolved) return false
    return providerToChannelKey(resolved) === channelKey
}

export function getChannelConfig(provider?: string | null): ChatChannelConfig {
    return CHAT_CHANNELS[providerToChannelKey(provider)]
}

export function getChannelFilterColor(key: ChatChannelKey): string {
    if (key === 'all') return CHAT_FILTER_ALL_COLOR
    return CHAT_CHANNELS[key].color
}

export function findChannelAvatar(
    connectorsAvatars: ConnectorAvatarLike[],
    key: ChatChannelKey
): ConnectorAvatarLike | null {
    if (key === 'all') return null
    const avatarType = CHAT_CHANNELS[key].avatarType
    return connectorsAvatars.find((avatar) => avatar.type === avatarType) || null
}

export function findChannelAvatarByProvider(
    connectorsAvatars: ConnectorAvatarLike[],
    provider?: string | null
): ConnectorAvatarLike | null {
    return findChannelAvatar(connectorsAvatars, providerToChannelKey(provider))
}

export function getChannelLabel(provider?: string | null): string {
    return getChannelConfig(provider).name
}

export function initials(name?: string | null): string {
    if (!name) return '?'
    const parts = name.trim().split(/\s+/).filter(Boolean)
    if (!parts.length) return '?'
    const first = parts[0][0] || ''
    const second = parts[1] ? parts[1][0] : ''
    return (first + second).toUpperCase()
}

export function avatarColor(seed: string): string {
    let hash = 0
    for (let i = 0; i < seed.length; i++) {
        hash = (hash + seed.charCodeAt(i)) % AVATAR_COLORS.length
    }
    return AVATAR_COLORS[hash]
}
